import { ARROW_DOWN, ARROW_UP, INFO } from "@components/atoms/Icons"
import {
  EVTableContainer,
  FilterBlock,
  FloatingFilter,
  InnerBlockContainer,
  Switch,
  SwitchRow,
  SwitchSelection,
  TT,
  TableWrapper,
  TickDate,
  Timeline,
  TimelineSelection,
  vehicleTypes,
} from "@components/atoms/data/Components"
import { Grid, Col } from "@components/layout/Grid"
import { VSpace } from "@components/layout/Spacer"
import { useEvAvailability, useEvData, useGlossaryTerms } from "@hooks/queries"
import { extractQuarterlyData, groupedByType } from "@utils/evDataHelpers"
import { EditableText, InnerBlocks, defineBlock } from "eddev/blocks"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { Tooltip } from "react-tooltip"
import { AnimatePresence, useInView } from "framer-motion"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Button } from "@components/atoms/Button"
import { CarTypeFilter } from "@components/atoms/data/CarTypeFilter"

export const meta: BlockMeta = {
  title: "EV Index Sales by Vehicle type and Fuel type",
  category: "common",
  icon: "editor-table",
  keywords: ["ev index"],
  tags: ["root"],
  editMode: "preview",
  conditions: {
    postTypes: ["page", "post", "data-embed"],
    multiple: false,
  },
}

type Obj = {
  "FUEL TYPE": string
  MANUFACTURER: string
  MODEL: string
  STATE: string
  "VEHICLE TYPE": string
  [key: string]: any
}

type SortConfig = {
  key: string // Allows sorting by "All fuels" as well
  direction: "ascending" | "descending"
}

export default defineBlock("data/evindex-sales-by-vehicle-and-fuel", (props) => {
  const evData = useEvData(undefined)
  const evAvailability = useEvAvailability(undefined)
  const glossary = useGlossaryTerms(undefined).data?.siteSettings?.themeSiteSettings?.glossary
  const [currentPeriod, setCurrentPeriod] = useState<number | null>(null)
  const [salesAvailability, setSalesAvailability] = useState<boolean>(false)
  const [currentVehicleType, setCurrentVehicleType] = useState<(keyof typeof vehicleTypes)[]>(["all"])
  const [rolling, setRolling] = useState<"normal" | "rolling" | "ytd">("normal")
  const [currentYear, setCurrentYear] = useState<number | null>(null)

  const parsedData = useMemo(() => {
    if (evData.data?.evIndexData && evAvailability.data?.evIndexDataAvailability) {
      return {
        sales: groupedByType(evData.data.evIndexData),
        availability: groupedByType(evAvailability.data.evIndexDataAvailability),
      }
    }
  }, [evData.data?.evIndexData, evAvailability.data?.evIndexDataAvailability])

  const quarters = useMemo(() => {
    if (parsedData) {
      const use = salesAvailability ? parsedData.availability : parsedData.sales
      return extractQuarterlyData(use[0].vehicles[0]).map((data) => data.quarter)
    }
    return []
  }, [parsedData])

  useEffect(() => {
    // Initial current quarter
    if (quarters.length > 0) {
      setCurrentPeriod(quarters.length - 1)
      setCurrentYear(parseInt(quarters[quarters.length - 1].split(" ")[1]))
    }
  }, [quarters])

  // State for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "VEHICLE TYPE",
    direction: "ascending",
  })

  const calculatePercentage = (
    data: Obj[],
    fuelType: "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV" | "all",
    totalSales: number,
  ) => {
    if (rolling === "rolling") {
      const value =
        fuelType === "all"
          ? (data.reduce(
              (a, b) =>
                a +
                b[quarters[currentPeriod!]] +
                b[quarters[currentPeriod! - 1]] +
                b[quarters[currentPeriod! - 2]] +
                b[quarters[currentPeriod! - 3]],
              0,
            ) /
              totalSales) *
            100
          : (data
              .filter((type) => type["FUEL TYPE"] === fuelType)
              .reduce(
                (a, b) =>
                  a +
                  b[quarters[currentPeriod!]] +
                  b[quarters[currentPeriod! - 1]] +
                  b[quarters[currentPeriod! - 2]] +
                  b[quarters[currentPeriod! - 3]],
                0,
              ) /
              totalSales) *
            100
      if (value === 0) return ""
      return `${value.toFixed(2)}%`
    } else if (rolling === "normal") {
      const value =
        fuelType === "all"
          ? (data.reduce((a, b) => a + b[quarters[currentPeriod!]], 0) / totalSales) * 100
          : (data
              .filter((type) => type["FUEL TYPE"] === fuelType)
              .reduce((a, b) => a + b[quarters[currentPeriod!]], 0) /
              totalSales) *
            100
      if (value === 0) return ""
      return `${value.toFixed(2)}%`
    } else if (rolling === "ytd") {
      if (!currentYear) return ""
      // Extract quarters for the current year
      const currentYearQuarters = quarters.filter((q) => q.includes(currentYear.toString()))

      // Sum total sales for all data in the current year
      const totalSalesForYear = data.reduce(
        (sum, entry) =>
          sum + currentYearQuarters.reduce((quarterSum, quarter) => quarterSum + (entry[quarter] || 0), 0),
        0,
      )

      // Calculate sales percentage for the selected fuelType
      const value =
        fuelType === "all"
          ? (totalSalesForYear / totalSales) * 100
          : (data
              .filter((entry) => entry["FUEL TYPE"] === fuelType)
              .reduce(
                (sum, entry) =>
                  sum + currentYearQuarters.reduce((quarterSum, quarter) => quarterSum + (entry[quarter] || 0), 0),
                0,
              ) /
              totalSales) *
            100

      // Return the formatted percentage or an empty string if the value is zero
      return value === 0 ? "" : `${value.toFixed(2)}%`
    }
  }

  const calculatePercentageTotals = (
    data: {
      "VEHICLE TYPE": string
      vehicles: Obj[]
    }[],
    fuelType: "ICE" | "BEV" | "PHEV" | "Hybrid" | "HFCEV",
  ) => {
    if (rolling === "rolling") {
      const value =
        (data.reduce(
          (a, b) =>
            a +
            b.vehicles
              .filter((type) => type["FUEL TYPE"] === fuelType)
              .reduce(
                (c, d) =>
                  c +
                  d[quarters[currentPeriod!]] +
                  d[quarters[currentPeriod! - 1]] +
                  d[quarters[currentPeriod! - 2]] +
                  d[quarters[currentPeriod! - 3]],
                0,
              ),
          0,
        ) /
          data.reduce(
            (a, b) =>
              a +
              b.vehicles.reduce(
                (c, d) =>
                  c +
                  d[quarters[currentPeriod!]] +
                  d[quarters[currentPeriod! - 1]] +
                  d[quarters[currentPeriod! - 2]] +
                  d[quarters[currentPeriod! - 3]],
                0,
              ),
            0,
          )) *
        100

      if (value === 0) return ""
      return `${value.toFixed(2)}%`
    } else if (rolling === "normal") {
      const value =
        (data.reduce(
          (a, b) =>
            a +
            b.vehicles
              .filter((type) => type["FUEL TYPE"] === fuelType)
              .reduce((c, d) => c + d[quarters[currentPeriod!]], 0),
          0,
        ) /
          data.reduce((a, b) => a + b.vehicles.reduce((c, d) => c + d[quarters[currentPeriod!]], 0), 0)) *
        100

      if (value === 0) return ""
      return `${value.toFixed(2)}%`
    } else if (rolling === "ytd") {
      if (!currentYear) return ""
      // Extract quarters for the current year
      const currentYearQuarters = quarters.filter((q) => q.includes(currentYear.toString()))

      // Sum sales for the selected fuel type across all vehicles in the current year
      const totalForFuelType = data.reduce(
        (sum, group) =>
          sum +
          group.vehicles
            .filter((vehicle) => vehicle["FUEL TYPE"] === fuelType)
            .reduce(
              (vehicleSum, vehicle) =>
                vehicleSum +
                currentYearQuarters.reduce((quarterSum, quarter) => quarterSum + (vehicle[quarter] || 0), 0),
              0,
            ),
        0,
      )

      // Sum sales for all fuel types across all vehicles in the current year
      const totalForAllTypes = data.reduce(
        (sum, group) =>
          sum +
          group.vehicles.reduce(
            (vehicleSum, vehicle) =>
              vehicleSum + currentYearQuarters.reduce((quarterSum, quarter) => quarterSum + (vehicle[quarter] || 0), 0),
            0,
          ),
        0,
      )

      // Calculate percentage
      const value = (totalForFuelType / totalForAllTypes) * 100

      // Return formatted percentage, or empty string if value is 0
      return value === 0 ? "" : `${value.toFixed(2)}%`
    }
  }

  // Function to sort the data based on the sort configuration
  const sortedData = useMemo(() => {
    if (!parsedData) return []
    if (typeof currentPeriod === "undefined" || currentPeriod === null) return []
    if (typeof currentYear === "undefined" || currentYear === null) return []
    const use = salesAvailability ? parsedData.availability : parsedData.sales
    const sorted = [...use].sort((a, b) => {
      let compareA, compareB

      if (sortConfig.key === "VEHICLE TYPE") {
        // Sort alphabetically by vehicle type
        compareA = a[sortConfig.key]
        compareB = b[sortConfig.key]
      } else {
        // Sort by total of specific fuel type
        if (rolling === "rolling") {
          compareA = a.vehicles
            .filter((vehicle) => vehicle["FUEL TYPE"] === (sortConfig.key as string))
            .reduce(
              (sum, vehicle) =>
                sum +
                (vehicle[quarters[currentPeriod!]] +
                  vehicle[quarters[currentPeriod! - 1]] +
                  vehicle[quarters[currentPeriod! - 2]] +
                  vehicle[quarters[currentPeriod! - 3]] || 0),
              0,
            )
          compareB = b.vehicles
            .filter((vehicle) => vehicle["FUEL TYPE"] === (sortConfig.key as string))
            .reduce(
              (sum, vehicle) =>
                sum +
                (vehicle[quarters[currentPeriod!]] +
                  vehicle[quarters[currentPeriod! - 1]] +
                  vehicle[quarters[currentPeriod! - 2]] +
                  vehicle[quarters[currentPeriod! - 3]] || 0),
              0,
            )
        } else if (rolling === "normal") {
          compareA = a.vehicles
            .filter((vehicle) => vehicle["FUEL TYPE"] === (sortConfig.key as string))
            .reduce((sum, vehicle) => sum + (vehicle[quarters[currentPeriod!]] || 0), 0)
          compareB = b.vehicles
            .filter((vehicle) => vehicle["FUEL TYPE"] === (sortConfig.key as string))
            .reduce((sum, vehicle) => sum + (vehicle[quarters[currentPeriod!]] || 0), 0)
        } else if (rolling === "ytd") {
          // Extract quarters for the current year
          const currentYearQuarters = quarters.filter((q) => q.includes(currentYear.toString()))

          // Calculate total sales for `compareA`
          compareA = a.vehicles
            .filter((vehicle) => vehicle["FUEL TYPE"] === (sortConfig.key as string))
            .reduce(
              (sum, vehicle) =>
                sum + currentYearQuarters.reduce((quarterSum, quarter) => quarterSum + (vehicle[quarter] || 0), 0),
              0,
            )

          // Calculate total sales for `compareB`
          compareB = b.vehicles
            .filter((vehicle) => vehicle["FUEL TYPE"] === (sortConfig.key as string))
            .reduce(
              (sum, vehicle) =>
                sum + currentYearQuarters.reduce((quarterSum, quarter) => quarterSum + (vehicle[quarter] || 0), 0),
              0,
            )
        }
      }

      if (compareA && compareB) {
        if (compareA < compareB) {
          return sortConfig.direction === "ascending" ? -1 : 1
        }
        if (compareA > compareB) {
          return sortConfig.direction === "ascending" ? 1 : -1
        }
      }
      return 0
    })

    if (!currentVehicleType.includes("all")) {
      const filtered: {
        "VEHICLE TYPE": string
        vehicles: Obj[]
      }[] = []

      currentVehicleType.forEach((type) => {
        sorted
          .filter((item) => item["VEHICLE TYPE"] === vehicleTypes[type])
          .forEach((result) => {
            filtered.push(result)
          })
      })

      return filtered
    }
    return sorted
  }, [parsedData, sortConfig, currentPeriod, currentVehicleType, salesAvailability, rolling, currentYear])

  // Handler for sorting
  const requestSort = (key: string) => {
    let direction: "ascending" | "descending" = "ascending"
    if (key !== "VEHICLE TYPE") {
      direction = "descending" // Set default to descending for all other columns
    }
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "ascending" ? "descending" : "ascending" // Toggle direction if the same column is clicked again
    }
    setSortConfig({ key, direction })
  }

  const totalSales = useMemo(() => {
    if (!sortedData) return null
    if (rolling === "rolling") {
      return sortedData.reduce(
        (a, b) =>
          a +
          b.vehicles.reduce(
            (c, d) =>
              c +
              d[quarters[currentPeriod!]] +
              d[quarters[currentPeriod! - 1]] +
              d[quarters[currentPeriod! - 2]] +
              d[quarters[currentPeriod! - 3]],
            0,
          ),
        0,
      )
    } else if (rolling === "normal") {
      return sortedData.reduce((a, b) => a + b.vehicles.reduce((c, d) => c + d[quarters[currentPeriod!]], 0), 0)
    } else if (rolling === "ytd") {
      if (!currentYear) return null
      // Extract quarters for the current year
      const currentYearQuarters = quarters.filter((q) => q.includes(currentYear.toString()))

      // Reduce over sortedData to sum up sales for the current year
      return sortedData.reduce(
        (totalSum, group) =>
          totalSum +
          group.vehicles.reduce(
            (vehicleSum, vehicle) =>
              vehicleSum + currentYearQuarters.reduce((quarterSum, quarter) => quarterSum + (vehicle[quarter] || 0), 0),
            0,
          ),
        0,
      )
    }
  }, [sortedData, rolling])

  const isMobile = useIsMobile()
  const [filterOpen, setFilterOpen] = useState<boolean>(false)
  const blockRef = useRef<HTMLDivElement>(null)
  const inView = useInView(blockRef, { amount: 0.5 })

  const [titleMessage, setTitleMessage] = useState<number>(0)
  useEffect(() => {
    // 1 !salesAvailability
    // 2 salesAvailability
    if (rolling !== "rolling" && !salesAvailability) setTitleMessage(1)
    if (rolling !== "rolling" && salesAvailability) setTitleMessage(2)
  }, [rolling, salesAvailability])

  return (
    <>
      <InnerBlockContainer
        className={`
          [&>div]:hidden [&>div_.block-editor-inner-blocks]:w-full [&>div.--grid]:w-full [&>div_.--grid]:w-full
          ${titleMessage === 1 ? "[&>div:nth-of-type(1)]:flex" : "[&>div:nth-of-type(1)]:hidden"}
          ${titleMessage === 2 ? "[&>div:nth-of-type(2)]:flex" : "[&>div:nth-of-type(2)]:hidden"}
        `}
      >
        <InnerBlocks />
      </InnerBlockContainer>
      <EVTableContainer ref={blockRef}>
        <div className="grid-auto px-4 md:px-6 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px]">
          <Col initial={12} laptop={6}>
            {salesAvailability ? (
              <EditableText as="p" placeholder="Enter content..." store="content_av" />
            ) : (
              <EditableText as="p" placeholder="Enter content..." store="content" />
            )}
            <VSpace initial={6} />
          </Col>

          {isMobile && (
            <AnimatePresence>
              {inView && (
                <FloatingFilter
                  open={filterOpen}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Button
                    size="tiny"
                    color="yellow"
                    icon={filterOpen ? "CARET_DOWN" : "CARET_UP"}
                    iconStyle="right"
                    onClick={() => setFilterOpen(!filterOpen)}
                  >
                    Filter this data
                  </Button>

                  <CarTypeFilter
                    currentVehicleType={currentVehicleType}
                    setCurrentVehicleType={setCurrentVehicleType}
                  />

                  <Switch>
                    <div>
                      <SwitchRow>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="sales"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setSalesAvailability(false)
                                  setRolling("normal")
                                }
                              }}
                            />
                            Sales
                          </label>
                        </SwitchSelection>

                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="availability"
                              onChange={(e) => {
                                if (e.currentTarget.checked) setSalesAvailability(true)
                              }}
                            />
                            Availability
                          </label>
                        </SwitchSelection>
                      </SwitchRow>
                    </div>
                  </Switch>
                  {!salesAvailability && (
                    <Switch>
                      <div>
                        <SwitchRow>
                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="quarterly"
                                defaultChecked
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("normal")
                                  }
                                }}
                              />
                              Quarterly
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="rolling"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("rolling")
                                  }
                                }}
                              />
                              Rolling 4 Quarters
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="ytd"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("ytd")
                                  }
                                }}
                              />
                              Year to date
                            </label>
                          </SwitchSelection>
                        </SwitchRow>
                      </div>
                    </Switch>
                  )}

                  <TimelineSelection light>
                    {quarters.length > 0 && currentPeriod !== null && currentYear !== null && (
                      <Timeline className="ml-0">
                        <ul>
                          {(() => {
                            // Extract unique years from quarters
                            const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                            years.sort((a, b) => parseInt(a) - parseInt(b))

                            return rolling === "ytd"
                              ? years.map((year, i) => (
                                  <li key={i}>
                                    <TickDate
                                      className={year === currentYear.toString() ? "opacity-100" : "opacity-50"}
                                    >
                                      {year}
                                    </TickDate>
                                  </li>
                                ))
                              : quarters.map((period, i) => {
                                  if (rolling === "rolling" && [0, 1, 2].includes(i)) return <Fragment key={i} />
                                  return (
                                    <li key={i}>
                                      <TickDate
                                        className={
                                          i === currentPeriod
                                            ? "opacity-100"
                                            : i === 0
                                              ? currentPeriod === 1
                                                ? "opacity-0"
                                                : "opacity-50"
                                              : "opacity-0"
                                        }
                                      >
                                        {rolling === "rolling" ? (
                                          <>
                                            {quarters[i - 3]} - {period}
                                          </>
                                        ) : (
                                          period
                                        )}
                                      </TickDate>
                                    </li>
                                  )
                                })
                          })()}
                        </ul>

                        {rolling === "ytd" && (
                          <input
                            key="ytd"
                            type="range"
                            min={0}
                            max={(() => {
                              const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                              years.sort((a, b) => parseInt(a) - parseInt(b))
                              return years.length - 1
                            })()}
                            step={1}
                            defaultValue={currentYear}
                            list="period-marks"
                            onChange={(e) => {
                              const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                              years.sort((a, b) => parseInt(a) - parseInt(b))
                              setCurrentYear(parseInt(years[parseInt(e.target.value)]))
                            }}
                          />
                        )}
                        {rolling === "rolling" && (
                          <input
                            key="rolling"
                            type="range"
                            min={3}
                            max={quarters.length - 1}
                            step={1}
                            defaultValue={quarters.length - 1}
                            list="period-marks"
                            onChange={(e) => {
                              setCurrentPeriod(parseInt(e.target.value))
                            }}
                          />
                        )}

                        {rolling === "normal" && (
                          <input
                            key="normal"
                            type="range"
                            min={0}
                            max={quarters.length - 1}
                            step={1}
                            defaultValue={quarters.length - 1}
                            list="period-marks"
                            onChange={(e) => {
                              setCurrentPeriod(parseInt(e.target.value))
                            }}
                          />
                        )}
                      </Timeline>
                    )}
                  </TimelineSelection>
                </FloatingFilter>
              )}
            </AnimatePresence>
          )}
          {!isMobile && (
            <>
              <Col initial={12}>
                <FilterBlock>
                  <CarTypeFilter
                    currentVehicleType={currentVehicleType}
                    setCurrentVehicleType={setCurrentVehicleType}
                  />

                  <Switch>
                    <div>
                      <SwitchRow>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="sales"
                              defaultChecked
                              onChange={(e) => {
                                if (e.currentTarget.checked) {
                                  setSalesAvailability(false)
                                  setRolling("normal")
                                }
                              }}
                            />
                            Sales
                          </label>
                        </SwitchSelection>
                      </SwitchRow>

                      <SwitchRow border>
                        <SwitchSelection>
                          <label>
                            <input
                              type="radio"
                              name="salesAvailability"
                              value="availability"
                              onChange={(e) => {
                                if (e.currentTarget.checked) setSalesAvailability(true)
                              }}
                            />
                            Availability
                          </label>
                        </SwitchSelection>
                      </SwitchRow>
                    </div>
                  </Switch>
                </FilterBlock>
              </Col>

              <Col initial={12}>
                <TimelineSelection light>
                  {!salesAvailability && (
                    <Switch>
                      <div>
                        <SwitchRow>
                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="quarterly"
                                defaultChecked
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("normal")
                                  }
                                }}
                              />
                              Quarterly
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="rolling"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("rolling")
                                  }
                                }}
                              />
                              Rolling 4 Quarters
                            </label>
                          </SwitchSelection>

                          <SwitchSelection>
                            <label>
                              <input
                                type="radio"
                                name="states"
                                value="ytd"
                                onChange={(e) => {
                                  if (e.currentTarget.checked) {
                                    setCurrentPeriod(quarters.length - 1)
                                    setRolling("ytd")
                                  }
                                }}
                              />
                              Year to date
                            </label>
                          </SwitchSelection>
                        </SwitchRow>
                      </div>
                    </Switch>
                  )}
                  {quarters.length > 0 && currentPeriod !== null && currentYear !== null && (
                    <Timeline className="ml-0">
                      <ul>
                        {(() => {
                          // Extract unique years from quarters
                          const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                          years.sort((a, b) => parseInt(a) - parseInt(b))

                          return rolling === "ytd"
                            ? years.map((year, i) => (
                                <li key={i}>
                                  <TickDate className={year === currentYear.toString() ? "opacity-100" : "opacity-50"}>
                                    {year}
                                  </TickDate>
                                </li>
                              ))
                            : quarters.map((period, i) => {
                                if (rolling === "rolling" && [0, 1, 2].includes(i)) return <Fragment key={i} />
                                return (
                                  <li key={i}>
                                    <TickDate
                                      className={
                                        i === currentPeriod
                                          ? "opacity-100"
                                          : i === 0
                                            ? currentPeriod === 1
                                              ? "opacity-0"
                                              : "opacity-50"
                                            : "opacity-0"
                                      }
                                    >
                                      {rolling === "rolling" ? (
                                        <>
                                          {quarters[i - 3]} - {period}
                                        </>
                                      ) : (
                                        period
                                      )}
                                    </TickDate>
                                  </li>
                                )
                              })
                        })()}
                      </ul>

                      {rolling === "ytd" && (
                        <input
                          key="ytd"
                          type="range"
                          min={0}
                          max={(() => {
                            const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                            years.sort((a, b) => parseInt(a) - parseInt(b))
                            return years.length - 1
                          })()}
                          step={1}
                          defaultValue={currentYear}
                          list="period-marks"
                          onChange={(e) => {
                            const years = [...new Set(quarters.map((q) => q.split(" ")[1]))]
                            years.sort((a, b) => parseInt(a) - parseInt(b))
                            setCurrentYear(parseInt(years[parseInt(e.target.value)]))
                          }}
                        />
                      )}
                      {rolling === "rolling" && (
                        <input
                          key="rolling"
                          type="range"
                          min={3}
                          max={quarters.length - 1}
                          step={1}
                          defaultValue={quarters.length - 1}
                          list="period-marks"
                          onChange={(e) => {
                            setCurrentPeriod(parseInt(e.target.value))
                          }}
                        />
                      )}

                      {rolling === "normal" && (
                        <input
                          key="normal"
                          type="range"
                          min={0}
                          max={quarters.length - 1}
                          step={1}
                          defaultValue={quarters.length - 1}
                          list="period-marks"
                          onChange={(e) => {
                            setCurrentPeriod(parseInt(e.target.value))
                          }}
                        />
                      )}
                    </Timeline>
                  )}
                </TimelineSelection>
              </Col>
            </>
          )}
          <Col initial={12} laptop={6} />
          {sortedData && totalSales && (
            <Col initial={12}>
              <TableWrapper>
                <div>
                  <table>
                    {glossary && (
                      <thead>
                        <tr>
                          <th onClick={() => requestSort("VEHICLE TYPE")} className="sortable">
                            <TT>
                              Vehicle type
                              {sortConfig.key === "VEHICLE TYPE" ? (
                                <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                              ) : (
                                <i>{ARROW_DOWN}</i>
                              )}
                            </TT>
                          </th>
                          <th onClick={() => requestSort("ICE")} className="sortable">
                            <TT>
                              ICE <a className="tt-ice">{INFO}</a>
                              <Tooltip anchorSelect=".tt-ice" style={{ width: "250px" }}>
                                {glossary.filter((term) => term?.term === "ICE")[0]?.description}
                              </Tooltip>
                              {sortConfig.key === "ICE" ? (
                                <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                              ) : (
                                <i>{ARROW_DOWN}</i>
                              )}
                            </TT>
                          </th>
                          <th onClick={() => requestSort("BEV")} className="sortable">
                            <TT>
                              BEV <a className="tt-bev">{INFO}</a>
                              <Tooltip anchorSelect=".tt-bev" style={{ width: "250px" }}>
                                {glossary.filter((term) => term?.term === "BEV")[0]?.description}
                              </Tooltip>
                              {sortConfig.key === "BEV" ? (
                                <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                              ) : (
                                <i>{ARROW_DOWN}</i>
                              )}
                            </TT>
                          </th>
                          <th onClick={() => requestSort("PHEV")} className="sortable">
                            <TT>
                              PHEV <a className="tt-phev">{INFO}</a>
                              <Tooltip anchorSelect=".tt-phev" style={{ width: "250px" }}>
                                {glossary.filter((term) => term?.term === "PHEV")[0]?.description}
                              </Tooltip>
                              {sortConfig.key === "PHEV" ? (
                                <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                              ) : (
                                <i>{ARROW_DOWN}</i>
                              )}
                            </TT>
                          </th>
                          <th onClick={() => requestSort("Hybrid")} className="sortable">
                            <TT>
                              Hybrid <a className="tt-hybrid">{INFO}</a>
                              <Tooltip anchorSelect=".tt-hybrid" style={{ width: "250px" }}>
                                {glossary.filter((term) => term?.term === "Hybrid")[0]?.description}
                              </Tooltip>
                              {sortConfig.key === "Hybrid" ? (
                                <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                              ) : (
                                <i>{ARROW_DOWN}</i>
                              )}
                            </TT>
                          </th>
                          <th onClick={() => requestSort("HFCEV")} className="sortable">
                            <TT>
                              HFCEV <a className="tt-hfcev">{INFO}</a>
                              <Tooltip anchorSelect=".tt-hfcev" style={{ width: "250px" }}>
                                {glossary.filter((term) => term?.term === "HFCEV")[0]?.description}
                              </Tooltip>
                              {sortConfig.key === "HFCEV" ? (
                                <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                              ) : (
                                <i>{ARROW_DOWN}</i>
                              )}
                            </TT>
                          </th>
                          <th>
                            <TT>
                              All fuels
                              {sortConfig.key === "All fuels" && (
                                <>{sortConfig.direction === "ascending" ? ARROW_UP : ARROW_DOWN}</>
                              )}
                            </TT>
                          </th>
                        </tr>
                      </thead>
                    )}
                    <tbody>
                      {sortedData.map((data, i) => (
                        <tr key={i}>
                          <td>{data["VEHICLE TYPE"]}</td>
                          <td>{calculatePercentage(data.vehicles, "ICE", totalSales)}</td>
                          <td>{calculatePercentage(data.vehicles, "BEV", totalSales)}</td>
                          <td>{calculatePercentage(data.vehicles, "PHEV", totalSales)}</td>
                          <td>{calculatePercentage(data.vehicles, "Hybrid", totalSales)}</td>
                          <td>{calculatePercentage(data.vehicles, "HFCEV", totalSales)}</td>
                          <td>{calculatePercentage(data.vehicles, "all", totalSales)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>All vehicle types</td>
                        <td>{calculatePercentageTotals(sortedData, "ICE")}</td>
                        <td>{calculatePercentageTotals(sortedData, "BEV")}</td>
                        <td>{calculatePercentageTotals(sortedData, "PHEV")}</td>
                        <td>{calculatePercentageTotals(sortedData, "Hybrid")}</td>
                        <td>{calculatePercentageTotals(sortedData, "HFCEV")}</td>
                        <td>100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </TableWrapper>
            </Col>
          )}
        </div>
      </EVTableContainer>
    </>
  )
})
